import type { CreateCustomOrderRequest, CustomOrderAssetRef } from './api/customOrdersApi';
import type { AssetEntry } from '../../shared/components/MediaUploader';

/**
 * PE-2285 — payload builder for the admin "new custom order" flow.
 *
 * The bytes go up first via `POST /media/upload` (multipart); this builder turns the
 * uploader's rows into the JSON `assets` refs that `POST /custom-orders` expects
 * (`CreateCustomOrderDto.assets` → `{ objectKey, bucket, mimeType, assetType }`).
 *
 * ⚠️ BACKEND DEPENDENCY: custom-order assets are read back through SIGNED PRIVATE urls
 * (osprey-api custom-orders.service.ts), and media-service selects private storage from
 * an `x-access: private` request header — but osprey-api's ProxyService header allowlist
 * (`src/common/proxy.service.ts`) does not forward `x-access`, so everything uploaded
 * through the BFF lands in the PUBLIC bucket. The refs built here are correct; making the
 * objects private needs osprey-api to forward `x-access`. Filed separately — do not
 * work around it here.
 */

/** media-service's assetType taxonomy, derived from the file's MIME type. */
export function assetTypeFromMimeType(mimeType: string | undefined | null): string {
  const mt = (mimeType ?? '').toLowerCase();
  if (mt.startsWith('image/')) return 'image';
  if (mt.startsWith('video/')) return 'video';
  if (mt.startsWith('audio/')) return 'audio';
  return 'file';
}

/**
 * Map uploader rows to DTO asset refs. Rows still uploading (no `objectKey`/`bucket`
 * yet) are dropped rather than sent half-built — every field on the ref DTO is
 * `@IsString() @IsNotEmpty()`, so a placeholder would 400 the whole create.
 */
export function toAssetRefs(assets: AssetEntry[]): CustomOrderAssetRef[] {
  return assets
    .filter((a) => !!a.objectKey && !!a.bucket)
    .map((a) => {
      // The uploader only accepts image/* and video/*, so fall back to its own
      // IMAGE|VIDEO kind when the browser gave us no MIME type for the file.
      const fallbackMime = a.type === 'VIDEO' ? 'video/mp4' : 'image/jpeg';
      const mimeType = a.mimeType || fallbackMime;
      const assetType = assetTypeFromMimeType(mimeType);
      return {
        objectKey: a.objectKey as string,
        bucket: a.bucket as string,
        mimeType,
        assetType: assetType === 'file' ? assetTypeFromMimeType(fallbackMime) : assetType,
      };
    });
}

export interface CustomOrderFormValues {
  customerEmail: string;
  solutionId: string;
  intake: Record<string, unknown>;
  assets: AssetEntry[];
  customerId?: string;
  bookingId?: string;
}

/** Build the `POST /custom-orders` body from the modal's form state. */
export function buildCreateCustomOrderPayload(values: CustomOrderFormValues): CreateCustomOrderRequest {
  const assets = toAssetRefs(values.assets);
  const body: CreateCustomOrderRequest = {
    solutionId: values.solutionId.trim(),
    customerEmail: values.customerEmail.trim(),
    intake: values.intake ?? {},
  };
  if (values.customerId?.trim()) body.customerId = values.customerId.trim();
  if (values.bookingId?.trim()) body.bookingId = values.bookingId.trim();
  // `assets` is @IsOptional upstream; omit it entirely when nothing uploaded rather
  // than sending an empty array (order-service would otherwise write an empty set and
  // the review screen's "Assets (0)" would read as "upload attempted, nothing stored").
  if (assets.length > 0) body.assets = assets;
  return body;
}
